import { buildApiUrl } from '../utils/env';

interface BrowserInfo {
  browser: string;
  platform: string;
  language: string;
  screenResolution: string;
  viewport: { width: number; height: number };
  outerSize: { width: number; height: number };
  colorDepth?: number;
  pixelDepth?: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };
  battery?: {
    charging?: boolean;
    level?: number;
  };
}

interface PerformanceData {
  navigationType?: string;
  timeToFirstByte?: number;
  domContentLoaded?: number;
  loadEvent?: number;
}

interface NetworkInformationDetails {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

type ExtendedNavigator = Navigator & {
  connection?: NetworkInformationDetails;
  mozConnection?: NetworkInformationDetails;
  webkitConnection?: NetworkInformationDetails;
  deviceMemory?: number;
  getBattery?: () => Promise<BatteryManager>;
};

type RawAnalyticsSession = Record<string, unknown> & {
  pageViews?: string | string[];
};

interface RawAnalyticsPerformance {
  avgLoadEvent?: number;
  [key: string]: unknown;
}

interface RawAnalyticsResponse {
  activeSessions?: RawAnalyticsSession[];
  totalPageviews?: number;
  dailyPageviews?: unknown[];
  topPages?: unknown[];
  byPlatform?: unknown[];
  byBrowser?: unknown[];
  byCountry?: unknown[];
  totalSessions?: number;
  dailySessions?: unknown[];
  performanceAverages?: RawAnalyticsPerformance;
  recentVisitors?: unknown[];
  topReferrers?: unknown[];
  recentPageviews?: unknown[];
  [key: string]: unknown;
}

type AnalyticsSessionRecord = Omit<RawAnalyticsSession, 'pageViews'> & {
  pageViews: string[];
};

interface AnalyticsData {
  pageViews: {
    total: number;
    daily: unknown[];
    byPage: unknown[];
    byDevice: unknown[];
    byBrowser: unknown[];
    byCountry: unknown[];
  };
  sessions: {
    total: number;
    daily: unknown[];
    averageSessionDuration: number;
    bounceRate: number;
  };
  visitors: {
    total: number;
    unique: number;
    returning: number;
    recent: unknown[];
    sessions: AnalyticsSessionRecord[];
    averageSessionDuration: number;
  };
  messages: {
    total: number;
    daily: unknown[];
    unread: number;
  };
  monitoring: {
    metrics: {
      total: number;
      errors: number;
      warnings: number;
      infos: number;
      lastHeartbeat: number | null;
      lastErrorAt: number | null;
    };
    logs: unknown[];
  };
  performance: {
    resources: {
      jsLoadTime: number;
      cssLoadTime: number;
      imageLoadTime: number;
    };
    errors: unknown[];
    loadTime: number;
    serverStatus: boolean;
    dbStatus: boolean;
    smtpStatus: boolean;
  };
  sources: {
    direct: number;
    referral: unknown[];
    search: unknown[];
    social: unknown[];
  };
  devices: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
  browsers: unknown[];
  countries: unknown[];
  logs: unknown[];
  interactions: {
    clicks: unknown[];
  };
}

class AnalyticsServerService {
  private sessionId: string | null = null;
  private landingPage: string | null = null;

  constructor() {
    // Generate or retrieve session ID from sessionStorage (survives refresh but not new tabs/windows)
    this.sessionId = sessionStorage.getItem('analytics_session_id');
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      sessionStorage.setItem('analytics_session_id', this.sessionId);
    }

    // Store landing page
    this.landingPage = sessionStorage.getItem('analytics_landing_page');
    if (!this.landingPage) {
      this.landingPage = window.location.pathname;
      sessionStorage.setItem('analytics_landing_page', this.landingPage);
    }
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  }

  private async collectBrowserInfo(): Promise<BrowserInfo> {
    // Detect browser
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Firefox/')) browser = 'Firefox';
    else if (ua.includes('Edg/')) browser = 'Edge';
    else if (ua.includes('Chrome/')) browser = 'Chrome';
    else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Opera/') || ua.includes('OPR/')) browser = 'Opera';

    const extendedNavigator = navigator as ExtendedNavigator;
    const connection =
      extendedNavigator.connection ||
      extendedNavigator.mozConnection ||
      extendedNavigator.webkitConnection;
    const battery = extendedNavigator.getBattery ? await extendedNavigator.getBattery().catch(() => null) : null;

    return {
      browser,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${screen.width}x${screen.height}`,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      outerSize: {
        width: window.outerWidth,
        height: window.outerHeight
      },
      colorDepth: screen.colorDepth,
      pixelDepth: screen.pixelDepth,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: extendedNavigator.deviceMemory,
      connection: connection
        ? {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData
        }
        : undefined,
      battery: battery
        ? {
          charging: battery.charging,
          level: battery.level
        }
        : undefined
    };
  }

  private collectPerformanceData(): PerformanceData | undefined {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (!navEntry) return undefined;

    return {
      navigationType: navEntry.type,
      timeToFirstByte: navEntry.responseStart - navEntry.requestStart,
      domContentLoaded: navEntry.domContentLoadedEventEnd - navEntry.startTime,
      loadEvent: navEntry.loadEventEnd - navEntry.startTime
    };
  }

  async startSession(): Promise<void> {
    try {
      const browserInfo = await this.collectBrowserInfo();

      await fetch(buildApiUrl('/api/analytics/session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sessionId: this.sessionId,
          landingPage: this.landingPage,
          referrer: document.referrer,
          browserInfo
        })
      });
    } catch (error) {
      console.warn('[Analytics] Failed to start session:', error);
    }
  }

  async trackPageView(path: string): Promise<void> {
    try {
      const browserInfo = await this.collectBrowserInfo();
      const performance = this.collectPerformanceData();
      const utmEntries = Object.fromEntries(new URLSearchParams(window.location.search).entries());
      const utm = utmEntries as Record<string, string>;

      await fetch(buildApiUrl('/api/analytics/pageview'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          path,
          referrer: document.referrer,
          landingPage: this.landingPage,
          sessionId: this.sessionId,
          browserInfo,
          performance,
          utm
        })
      });
    } catch (error) {
      console.warn('[Analytics] Failed to track pageview:', error);
    }
  }

  async updatePerformance(): Promise<void> {
    // No-op for now, could be used for timing metrics
  }

  async recordMonitoringLog(entry: unknown): Promise<void> {
    // No-op for compatibility with logger
    // Monitoring logs are sent to server directly via logger.sendToServer()
  }

  async getData(): Promise<AnalyticsData> {
    // Fetch from server instead of localStorage
    try {
      const response = await fetch(buildApiUrl('/api/analytics/stats'), {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = (await response.json()) as RawAnalyticsResponse;

      // Transform activeSessions data
      const transformedSessions: AnalyticsSessionRecord[] = (data.activeSessions ?? []).map(session => {
        const { pageViews, ...rest } = session;
        const normalizedPageViews = Array.isArray(pageViews)
          ? pageViews.map(view => String(view))
          : typeof pageViews === 'string'
            ? pageViews.split(',').map(view => view.trim()).filter(Boolean)
            : [];
        return {
          ...rest,
          pageViews: normalizedPageViews
        };
      });

      // Ensure all expected fields exist
      return {
        pageViews: {
          total: data.totalPageviews ?? 0,
          daily: data.dailyPageviews ?? [],
          byPage: data.topPages ?? [],
          byDevice: data.byPlatform ?? [],
          byBrowser: data.byBrowser ?? [],
          byCountry: data.byCountry ?? []
        },
        sessions: {
          total: data.totalSessions ?? 0,
          daily: data.dailySessions ?? [],
          averageSessionDuration: data.performanceAverages?.avgLoadEvent ?? 0,
          bounceRate: 0
        },
        visitors: {
          total: data.totalSessions ?? 0,
          unique: data.totalSessions ?? 0,
          returning: 0,
          recent: data.recentVisitors ?? [],
          sessions: transformedSessions,
          averageSessionDuration: data.performanceAverages?.avgLoadEvent ?? 0
        },
        messages: {
          total: 0,
          daily: [],
          unread: 0
        },
        monitoring: {
          metrics: {
            total: 0,
            errors: 0,
            warnings: 0,
            infos: 0,
            lastHeartbeat: null,
            lastErrorAt: null
          },
          logs: []
        },
        performance: {
          resources: {
            jsLoadTime: 0,
            cssLoadTime: 0,
            imageLoadTime: 0
          },
          errors: [],
          loadTime: data.performanceAverages?.avgLoadEvent || 0,
          serverStatus: true,
          dbStatus: true,
          smtpStatus: false
        },
        sources: {
          direct: 0,
          referral: data.topReferrers || [],
          search: [],
          social: []
        },
        devices: {
          desktop: 0,
          mobile: 0,
          tablet: 0
        },
        browsers: data.byBrowser ?? [],
        countries: data.byCountry ?? [],
        logs: data.recentPageviews ?? [],
        interactions: {
          clicks: []
        }
      } satisfies AnalyticsData;
    } catch (error) {
      console.warn('[Analytics] Failed to get data:', error);
      return {
        pageViews: { total: 0, daily: [], byPage: [], byDevice: [], byBrowser: [], byCountry: [] },
        sessions: { total: 0, daily: [], averageSessionDuration: 0, bounceRate: 0 },
        visitors: { total: 0, unique: 0, returning: 0, recent: [], sessions: [], averageSessionDuration: 0 },
        messages: { total: 0, daily: [], unread: 0 },
        monitoring: { metrics: { total: 0, errors: 0, warnings: 0, infos: 0, lastHeartbeat: null, lastErrorAt: null }, logs: [] },
        performance: { resources: { jsLoadTime: 0, cssLoadTime: 0, imageLoadTime: 0 }, errors: [], loadTime: 0, serverStatus: true, dbStatus: true, smtpStatus: false },
        sources: { direct: 0, referral: [], search: [], social: [] },
        devices: { desktop: 0, mobile: 0, tablet: 0 },
        browsers: [],
        countries: [],
        logs: [],
        interactions: { clicks: [] }
      } as AnalyticsData;
    }
  }
}

// Export singleton
export const analyticsServerService = new AnalyticsServerService();
